import React, { useEffect, useState, useRef } from 'react';
import { HeartIcon, MessageCircleIcon, BookmarkIcon, ShareIcon, VolumeIcon, Volume2Icon, PlayIcon, PauseIcon, InstagramIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
interface ReelProps {
  id: string;
  videoUrl: string;
  username: string;
  caption: {
    en: string;
    ar: string;
  };
  likes: number;
  comments: number;
  thumbnail: string;
  userAvatar: string;
  instagramUrl?: string;
}
export function InstagramReels() {
  const {
    isArabic
  } = useLanguage();
  const [currentReelIndex, setCurrentReelIndex] = useState(0);
  const [isLiked, setIsLiked] = useState<Record<string, boolean>>({});
  const [isBookmarked, setIsBookmarked] = useState<Record<string, boolean>>({});
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState<Record<string, boolean>>({});
  const reelRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const reels: ReelProps[] = [{
    id: '1',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-young-woman-talking-on-video-call-with-a-friend-40837-large.mp4',
    username: 'arabicwithenglish',
    caption: {
      en: 'Learn the most common greetings in English! 🇬🇧 #EnglishLearning #Greetings',
      ar: 'تعلم أكثر التحيات شيوعًا باللغة الإنجليزية! 🇬🇧 #تعلم_الإنجليزية #تحيات'
    },
    likes: 1245,
    comments: 87,
    thumbnail: 'https://images.unsplash.com/photo-1517960413843-0aee8e2b3285?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    userAvatar: 'https://i.pravatar.cc/150?img=1',
    instagramUrl: 'https://www.instagram.com/reel/CwfypLpt-kx/'
  }, {
    id: '2',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-teacher-pointing-at-an-english-text-27127-large.mp4',
    username: 'englishwitharabic',
    caption: {
      en: 'Master these 5 pronunciation tips to sound like a native speaker! 🎯 #EnglishPronunciation',
      ar: 'أتقن نصائح النطق الخمس هذه لتبدو مثل المتحدث الأصلي! 🎯 #نطق_إنجليزي'
    },
    likes: 892,
    comments: 53,
    thumbnail: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    userAvatar: 'https://i.pravatar.cc/150?img=2',
    instagramUrl: 'https://www.instagram.com/reel/C4eDSdkvB-s/'
  }, {
    id: '3',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-woman-having-an-online-class-with-a-laptop-40838-large.mp4',
    username: 'learnenglishtoday',
    caption: {
      en: 'Daily English phrases you NEED to know! 💬 Use these in conversation! #EnglishPhrases',
      ar: 'عبارات إنجليزية يومية تحتاج إلى معرفتها! 💬 استخدمها في المحادثة! #عبارات_إنجليزية'
    },
    likes: 2156,
    comments: 124,
    thumbnail: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    userAvatar: 'https://i.pravatar.cc/150?img=3',
    instagramUrl: 'https://www.instagram.com/p/DDAUNE7iev2/'
  }];
  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      const video = entry.target as HTMLVideoElement;
      const index = reelRefs.current.findIndex(ref => ref === video);
      if (entry.isIntersecting) {
        setCurrentReelIndex(index);
        if (video.paused) {
          video.play().then(() => {
            setIsPlaying(prev => ({
              ...prev,
              [reels[index].id]: true
            }));
          }).catch(error => console.log('Autoplay prevented:', error));
        }
      } else {
        video.pause();
        setIsPlaying(prev => ({
          ...prev,
          [reels[index].id]: false
        }));
      }
    });
  };
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.7
    });
    reelRefs.current.forEach(video => {
      if (video) observer.observe(video);
    });
    return () => {
      reelRefs.current.forEach(video => {
        if (video) observer.unobserve(video);
      });
    };
  }, []);
  const toggleMute = () => {
    setIsMuted(!isMuted);
    reelRefs.current.forEach(video => {
      if (video) video.muted = !isMuted;
    });
  };
  const toggleLike = (id: string) => {
    setIsLiked(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const toggleBookmark = (id: string) => {
    setIsBookmarked(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const togglePlayPause = (index: number) => {
    const video = reelRefs.current[index];
    const reel = reels[index];
    if (!video) return;
    if (video.paused) {
      video.play().then(() => {
        setIsPlaying(prev => ({
          ...prev,
          [reel.id]: true
        }));
      });
    } else {
      video.pause();
      setIsPlaying(prev => ({
        ...prev,
        [reel.id]: false
      }));
    }
  };
  return <div className="w-full bg-black" data-id="element-644">
      <div className="max-w-md mx-auto relative" data-id="element-645">
        <div className="snap-y snap-mandatory h-[80vh] overflow-y-scroll scrollbar-hide" data-id="element-646">
          {reels.map((reel, index) => <div key={reel.id} className="snap-start h-full w-full relative flex items-center justify-center" data-id="element-647">
              <div className="relative w-full h-full bg-black" data-id="element-648">
                <video ref={el => reelRefs.current[index] = el} src={reel.videoUrl} poster={reel.thumbnail} className="w-full h-full object-contain" loop muted={isMuted} playsInline onClick={() => togglePlayPause(index)} data-id="element-649" />
                <div className="absolute inset-0 flex items-center justify-center" onClick={() => togglePlayPause(index)} data-id="element-650">
                  {!isPlaying[reel.id] && reelRefs.current[index]?.paused && <div className="bg-black/30 rounded-full p-4" data-id="element-651">
                      <PlayIcon className="w-10 h-10 text-white" data-id="element-652" />
                    </div>}
                </div>
                {reel.instagramUrl && <a href={reel.instagramUrl} target="_blank" rel="noopener noreferrer" className="absolute top-4 left-4 flex items-center gap-1.5 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white text-xs font-medium px-2.5 py-1.5 rounded-full hover:opacity-90 transition-opacity" onClick={e => e.stopPropagation()} data-id="element-653">
                    <InstagramIcon className="w-3.5 h-3.5" data-id="element-654" />
                    <span data-id="element-655">View on Instagram</span>
                    <ExternalLinkIcon className="w-3 h-3" data-id="element-656" />
                  </a>}
                <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-657">
                  <div className="flex items-center mb-3" data-id="element-658">
                    <img src={reel.userAvatar} alt={reel.username} className="w-10 h-10 rounded-full border-2 border-white" data-id="element-659" />
                    <span className="ml-2 font-bold" data-id="element-660">{reel.username}</span>
                  </div>
                  <p className="text-sm mb-6" data-id="element-661">
                    {isArabic ? reel.caption.ar : reel.caption.en}
                  </p>
                </div>
                <div className="absolute right-4 bottom-20 flex flex-col items-center space-y-6" data-id="element-662">
                  <button onClick={() => toggleLike(reel.id)} className="flex flex-col items-center" aria-label={isLiked[reel.id] ? 'Unlike' : 'Like'} data-id="element-663">
                    <HeartIcon className={`w-7 h-7 ${isLiked[reel.id] ? 'text-red-500 fill-red-500' : 'text-white'}`} data-id="element-664" />
                    <span className="text-white text-xs mt-1" data-id="element-665">
                      {isLiked[reel.id] ? reel.likes + 1 : reel.likes}
                    </span>
                  </button>
                  <button className="flex flex-col items-center" aria-label="Comments" data-id="element-666">
                    <MessageCircleIcon className="w-7 h-7 text-white" data-id="element-667" />
                    <span className="text-white text-xs mt-1" data-id="element-668">
                      {reel.comments}
                    </span>
                  </button>
                  <button onClick={() => toggleBookmark(reel.id)} className="flex flex-col items-center" aria-label={isBookmarked[reel.id] ? 'Remove bookmark' : 'Bookmark'} data-id="element-669">
                    <BookmarkIcon className={`w-7 h-7 ${isBookmarked[reel.id] ? 'text-yellow-500 fill-yellow-500' : 'text-white'}`} data-id="element-670" />
                  </button>
                  <button className="flex flex-col items-center" aria-label="Share" data-id="element-671">
                    <ShareIcon className="w-7 h-7 text-white" data-id="element-672" />
                  </button>
                </div>
                <button onClick={toggleMute} className="absolute top-4 right-4 bg-black/50 p-2 rounded-full" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-673">
                  {isMuted ? <VolumeIcon className="w-5 h-5 text-white" data-id="element-674" /> : <Volume2Icon className="w-5 h-5 text-white" data-id="element-675" />}
                </button>
              </div>
            </div>)}
        </div>
        <div className="absolute top-2 left-16 right-2 flex space-x-1" data-id="element-676">
          {reels.map((_, index) => <div key={index} className={`h-1 rounded-full flex-1 ${index === currentReelIndex ? 'bg-white' : 'bg-white/30'}`} data-id="element-677" />)}
        </div>
      </div>
    </div>;
}